import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { trimStart } from 'lodash';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { StoreState } from '@mm/state/store-interfaces';
import TagsCRUDActions from '@mm/state/actions/TagsCRUDActions';
import FilterableSelect from '../../../../../../../common/components/FilterableSelect/FilterableSelect';
import TextInput from '../../../../../../../common/components/TextInput';
import './ButtonTagsForm.scss';
import TagModel from '@mm/state/model/TagModel';
import CheckBox from '../../../../../../../common/components/Checkbox';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { TagsWidgetConfig } from '@mm/state/model/WidgetModel';
import classnames from 'classnames';
import { tagsOfKinds } from '@mm/state/selectors/tags';
import { Radio } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { Button } from '@mm/components';

type Kind = TagsWidgetConfig['kind'];
type PropsFromState = ReturnType<typeof mapStateToProps>;
type PropsFromDispatch = ReturnType<typeof mapDispatchToProps>;
type ChangeKindHandler = (kind: Kind) => any;
type OuterProps = {
  title: string;
  isOpen?: boolean;
  onTagAdd: (tag: TagModel) => any;
  onTagRemove: (tag: TagModel) => any;
  onResetAll?: () => void;
  onChangeKind?: ChangeKindHandler;
  kind: Kind;
  tags: TagModel[];
};

type Props = PropsFromState & PropsFromDispatch & OuterProps;

type TagProps = {
  item: TagModel;
  onDelete: (e: TagModel) => any;
  className?: string;
};

const Tag = ({ item, onDelete, className }: TagProps) => {
  const onTagRemove = () => {
    onDelete && onDelete(item);
  };

  return (
    <div className={classnames('button-tags-form__tag', className)}>
      <div>{item.name}</div>
      <CloseOutlined
        className="button-tags-form__tag-remove"
        onClick={onTagRemove}
      />
    </div>
  );
};

const ButtonTagsFormPure = ({
  tags,
  title,
  onTagAdd,
  onTagRemove,
  onChangeKind,
  kind,
  availableTags,
  fbPageId,
  getAllTags,
  onResetAll,
  isOpen,
}: Props) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchQuery, setSearchQeury] = useState('');
  const [isFormVisible, setIsFormVisible] = useState(!!isOpen);
  const isAdd = kind === 'add_tags';
  const oneKind = !onChangeKind;

  const onSearchQuery = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      e.target.value = trimStart(e.target.value);
      setSearchQeury(e.target.value);
    },
    [],
  );

  const onDropdownToggle = useCallback(
    () => setIsDropdownOpen(!isDropdownOpen),
    [isDropdownOpen],
  );
  const onFormToggle = () => setIsFormVisible(!isFormVisible);

  const onSubmitForm = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isAdd) {
      return;
    }

    if (searchQuery) {
      onTagAdd({ name: searchQuery.trim(), unsaved: true, kind: 'regular' });
      setSearchQeury('');
    }
    onDropdownToggle();

    setSearchQeury('');
  };

  const onTagSelect = useCallback(
    (id: string, tag?: TagModel) => {
      if (tag) {
        onTagAdd(tag);
      }

      setSearchQeury('');
      if (isDropdownOpen) {
        onDropdownToggle();
      }
    },
    [isDropdownOpen],
  );

  useEffect(() => {
    if (fbPageId) {
      getAllTags(fbPageId);
    }
  }, [fbPageId]);

  const suggestions = useMemo(() => {
    return [
      {
        title: '',
        suggestions: tags
          ? availableTags.filter(e => !tags.some(tag => tag.name === e.name))
          : availableTags,
      },
    ];
  }, [availableTags, tags]);

  const wrapperClasses = classnames(
    'button-tags-form__input-wr',
    { 'button-tags-form__input-wr--remove': !isAdd },
    { 'button-tags-form__input-wr--multi': !oneKind },
  );

  const renderInputWithTags = (
    <div className="button-tags-form__input-with-tags">
      <div className="button-tags-form__input-row">
        <TextInput
          className="button-tags-form__input"
          name="name"
          value={searchQuery}
          autoComplete="false"
          readOnly
          placeholder={
            !oneKind ? 'Tag name' : `${isAdd ? 'Add' : 'Remove'} tag`
          }
          onFocus={onDropdownToggle}
        />
        {isAdd && (
          <Button type="submit" css={{ container: 'button-tags-form__btn' }}>
            Add
          </Button>
        )}
      </div>

      {!!tags.length && (
        <div className="button-tags-form__tag-list">
          {onResetAll && (
            <span className="button-tags-form__reset-all" onClick={onResetAll}>
              Reset all <CloseOutlined />
            </span>
          )}
          {tags.map(tag => (
            <Tag
              key={tag.name}
              item={tag}
              onDelete={onTagRemove}
              className={!isAdd ? 'button-tags-form__remove-tag' : ''}
            />
          ))}
        </div>
      )}
    </div>
  );

  return tags && tags.length === 0 && !isFormVisible ? (
    <CheckBox
      className="button-tag-form__checkbox"
      label={title}
      isChecked={false}
      onClick={onFormToggle}
    />
  ) : (
    <div className="button-tag-form">
      <form className="button-tag-form__form" onSubmit={onSubmitForm}>
        <div className={wrapperClasses}>
          {!oneKind && (
            <>
              <div className="button-tags-form__option-container">
                <Radio
                  className="button-tags-form__radio"
                  checked={isAdd}
                  onClick={() =>
                    (onChangeKind as ChangeKindHandler)('add_tags')
                  }
                >
                  Add a tag
                </Radio>
                {isAdd && renderInputWithTags}
              </div>
              <div className="button-tags-form__option-container">
                <Radio
                  className="button-tags-form__radio"
                  checked={!isAdd}
                  onClick={() =>
                    (onChangeKind as ChangeKindHandler)('remove_tags')
                  }
                >
                  Remove a tag
                </Radio>
                {!isAdd && renderInputWithTags}
              </div>
            </>
          )}
          {oneKind && (
            <label className="button-tags-form__label">
              <span>{isAdd ? 'Add' : 'Remove'} a tag</span>
              {renderInputWithTags}
            </label>
          )}
          {isDropdownOpen && (
            <FilterableSelect
              showSuggestOnly={true}
              defaultValue={searchQuery}
              className="button-tags-form__autocomplete"
              onBlur={onDropdownToggle}
              onInput={onSearchQuery}
              onChange={onTagSelect}
              name="filterable"
              searchPlaceholder={
                oneKind ? `${isAdd ? 'Add' : 'Remove'} tag` : 'Tag name'
              }
              suggestions={suggestions}
            />
          )}
        </div>
      </form>
    </div>
  );
};

const mapStateToProps = (state: StoreState) => ({
  availableTags: tagsOfKinds(state, ['regular']),
  fbPageId: activeFacebookPageIdSelector(state),
});

const mapDispatchToProps = (dispatch: Dispatch<StoreState>) => ({
  getAllTags: (pageId: number) =>
    dispatch(TagsCRUDActions.getAll(pageId, false, ['otn', 'regular'])),
});

export const ButtonTagsForm = connect<
  PropsFromState,
  PropsFromDispatch,
  OuterProps
>(
  mapStateToProps,
  mapDispatchToProps,
)(ButtonTagsFormPure);
